import { useForm } from "@tanstack/react-form";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAgent } from "agents/react";
import {
  AlertTriangle,
  Bot,
  BrainCircuit,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  CodeXml,
  Loader2,
  Logs,
  Settings,
  Terminal,
  Trash2,
  Wrench,
} from "lucide-react";
import { Suspense, lazy, memo, useCallback, useRef } from "react";
import { useState } from "react";
import type { TelemetrySpanCreatedEvent } from "../../../../schemas/events";
import { TelemetrySpanCreatedEventSchema } from "../../../../schemas/events";
import {
  getPavilionAgentQueryOptions,
  useDeletePavilionAgent,
  useUpdatePavilionAgent,
} from "../../../lib/hooks/usePavilionAgents";

// Lazy load the chat component to prevent blocking
const AgentChat = lazy(() =>
  import("../../../components/pavilion/agent-chat").then((module) => ({
    default: module.AgentChat,
  })),
);

// Memoized version to prevent unnecessary re-renders
const MemoizedAgentChat = memo(AgentChat);
import {
  AVAILABLE_MODELS,
  type AgentConfig,
  AgentConfigSchema,
} from "../../../../pavilion/schemas";
import { AgentLogsTable } from "../../../components/agent-logs-table";
import { McpServersTable } from "../../../components/pavilion/mcp-servers-table";
import { SlackIntegration } from "../../../components/pavilion/slack-integration";
import { ToolsTable } from "../../../components/pavilion/tools-table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Button } from "../../../components/ui/button";
import { Combobox } from "../../../components/ui/combobox";
import { FpSidebarTrigger } from "../../../components/ui/fp-sidebar";
import { Input } from "../../../components/ui/input";
import {
  FpTabs,
  FpTabsContent,
  FpTabsList,
  FpTabsTrigger,
} from "../../../components/ui/tabs";
import { Textarea } from "../../../components/ui/textarea";
import { usePavilionUiStore } from "../../../state/stores";
import type { Tab } from "../../../state/stores/types";

export const Route = createFileRoute("/(pavilion)/pavilion/$agentName")({
  validateSearch: (search) => {
    return {
      leftTab: (search.leftTab as string) || "system",
      rightTab: (search.rightTab as string) || "console",
    };
  },
  component: () => (
    <Suspense
      fallback={
        <div className="flex items-center justify-center h-full">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading agent details...
          </div>
        </div>
      }
    >
      <AgentDetail />
    </Suspense>
  ),
  loader: async ({ context: { queryClient }, params: { agentName } }) => {
    const agentResponse = await queryClient.ensureQueryData(
      getPavilionAgentQueryOptions(agentName),
    );
    return {
      agentData: agentResponse.data,
      agentName,
    };
  },
});

function AgentDetail() {
  const { agentName } = Route.useParams();
  const { agentData } = Route.useLoaderData();
  const navigate = useNavigate({ from: Route.fullPath });
  const { leftTab, rightTab } = Route.useSearch();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDangerZone, setShowDangerZone] = useState(false);
  const { activeTab, setActiveTab } = usePavilionUiStore();
  const queryClient = useQueryClient();

  // Agent data comes directly from the loader
  const agentConfig = agentData;

  // Mutation hooks
  const updateAgentMutation = useUpdatePavilionAgent();
  const deleteAgentMutation = useDeletePavilionAgent();

  // Use agent hooks for chat - middleware is at root level now
  // Use localhost for WebSocket in development when using tunnel domains
  const isDevTunnel = window.location.hostname.endsWith("fiberplane.io");
  const wsHost = isDevTunnel ? "localhost:7676" : undefined;

  const agent = useAgent({
    agent: "pavilion-agent",
    name: agentName,
    basePath: `api/agents/pavilion-agent/${agentName}`,
    ...(wsHost && { host: wsHost }),
    onMessage: (message) => {
      try {
        // Parse and validate the WebSocket message using the proper schema
        const rawData = JSON.parse(message.data);
        const parsedData = TelemetrySpanCreatedEventSchema.safeParse(rawData);

        if (!parsedData.success) {
          return; // Ignore invalid messages
        }

        const data: TelemetrySpanCreatedEvent = parsedData.data;

        // Handle telemetry updates from the agent WebSocket
        if (data.type === "telemetry.span.created") {
          // Add new telemetry data directly to cache for real-time updates
          queryClient.setQueryData(
            ["agent-telemetry", agentName, "", ""],
            (old: unknown) => {
              if (!old) {
                return old;
              }

              // Add the new span to the first page (most recent)
              const oldData = old as { pages: Array<{ data: unknown[] }> };
              const newPages = [...oldData.pages];
              if (newPages[0]) {
                newPages[0] = {
                  ...newPages[0],
                  data: [data.span, ...newPages[0].data],
                };
              }

              return {
                ...oldData,
                pages: newPages,
              };
            },
          );
        }
      } catch (error) {
        console.error("Failed to parse WebSocket message:", error);
      }
    },
  });

  // TanStack Form with Zod validation and autosave - gets fresh data on each route change
  const systemForm = useForm({
    defaultValues: {
      name: agentConfig.name,
      description: agentConfig.description || "",
      modelProvider: (agentConfig.modelProvider || "openai") as
        | "openai"
        | "anthropic",
      modelName: agentConfig.modelName || "gpt-4.1-2025-04-14",
      systemPrompt: agentConfig.systemPrompt || "",
      tools: agentConfig.tools || [],
    },
    onSubmit: async ({ value }) => {
      // Validate form data
      const validationResult = AgentConfigSchema.safeParse(value);
      if (!validationResult.success) {
        console.error("Form validation failed:", validationResult.error);
        return;
      }

      // Save using TanStack Query mutation
      await handleAutoSave(validationResult.data);
    },
  });

  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [editingField, setEditingField] = useState<string | null>(null);
  const autosaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to update tabs in URL
  const updateTabInUrl = useCallback(
    (tab: Tab) => {
      const isLeftPanelTab = ["system", "tools", "portals"].includes(tab);
      const isRightPanelTab = ["console", "logs", "api"].includes(tab);

      if (isLeftPanelTab) {
        navigate({
          search: (prev) => ({ ...prev, leftTab: tab }),
          replace: true,
        });
      } else if (isRightPanelTab) {
        navigate({
          search: (prev) => ({ ...prev, rightTab: tab }),
          replace: true,
        });
      }

      // Update store for mobile view
      setActiveTab(tab);
    },
    [navigate, setActiveTab],
  );

  // Debounced autosave function using TanStack Query mutations
  const debouncedAutoSave = useCallback(
    (formData: Partial<AgentConfig>) => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }

      autosaveTimeoutRef.current = setTimeout(async () => {
        setSaveStatus("saving");

        try {
          await updateAgentMutation.mutateAsync({
            agentName: agentConfig.name,
            data: formData,
          });

          setSaveStatus("saved");
          setTimeout(() => {
            setSaveStatus("idle");
            setEditingField(null);
          }, 4000);
        } catch (error) {
          console.error("❌ Save operation failed with error:", error);
          setSaveStatus("error");
          setTimeout(() => {
            setSaveStatus("idle");
            setEditingField(null);
          }, 5000);
        }
      }, 1000); // Wait 1 second after user stops typing
    },
    [agentConfig, updateAgentMutation],
  );

  // Immediate autosave function for dropdowns
  const handleAutoSave = useCallback(
    async (formData: Partial<AgentConfig>) => {
      setSaveStatus("saving");

      try {
        await updateAgentMutation.mutateAsync({
          agentName: agentConfig.name,
          data: formData,
        });

        setSaveStatus("saved");
        setTimeout(() => {
          setSaveStatus("idle");
          setEditingField(null);
        }, 4000);
      } catch (error) {
        console.error(
          "❌ [IMMEDIATE] Save operation failed with error:",
          error,
        );
        setSaveStatus("error");
        setTimeout(() => {
          setSaveStatus("idle");
          setEditingField(null);
        }, 5000);
      }
    },
    [agentConfig, updateAgentMutation],
  );

  // Save status indicator component - only shows for the field being edited
  const SaveStatusIndicator = useCallback(
    ({ fieldName }: { fieldName: string }) => {
      if (editingField !== fieldName) {
        return null;
      }

      switch (saveStatus) {
        case "saving":
          return (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Saving...
            </div>
          );
        case "saved":
          return (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle className="h-3 w-3" />
              Saved
            </div>
          );
        case "error":
          return (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <AlertTriangle className="h-3 w-3" />
              Save failed
            </div>
          );
        default:
          return null;
      }
    },
    [editingField, saveStatus],
  );

  const renderTabContent = useCallback(
    (tab: Tab) => {
      switch (tab) {
        case "system":
          return (
            <div className="space-y-6 h-full flex flex-col">
              {/* Agent Name */}
              <div className="space-y-2">
                <div className="text-sm font-medium">Agent name</div>
                <div className="px-3 py-2 border rounded-md bg-muted text-sm">
                  {agentConfig.name}
                </div>
              </div>

              {/* Delete Agent Section - Collapsible */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowDangerZone(!showDangerZone)}
                  className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showDangerZone ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                  <span>Delete agent</span>
                </button>
                {showDangerZone && (
                  <div className="mt-2 pl-5 space-y-2">
                    <p className="text-xs text-muted-foreground">
                      This will permanently delete the agent and all its data.
                    </p>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowDeleteDialog(true)}
                      className="flex items-center gap-2 h-7"
                    >
                      <Trash2 className="h-3 w-3" />
                      Delete
                    </Button>
                  </div>
                )}
              </div>

              {/* Description */}
              <systemForm.Field name="description">
                {(field) => (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label
                        className="text-sm font-medium"
                        htmlFor="description"
                      >
                        Description
                      </label>
                      <SaveStatusIndicator fieldName="description" />
                    </div>
                    <Input
                      id="description"
                      value={field.state.value || ""}
                      onChange={(e) => {
                        field.handleChange(e.target.value);
                        setEditingField("description");
                        debouncedAutoSave({
                          description: e.target.value,
                        });
                      }}
                      onBlur={field.handleBlur}
                      placeholder="Enter agent description"
                    />
                    {field.state.meta.errors && (
                      <div className="text-sm text-red-600">
                        {field.state.meta.errors.join(", ")}
                      </div>
                    )}
                  </div>
                )}
              </systemForm.Field>

              {/* Model Selection */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium" htmlFor="model">
                    Model
                  </label>
                  <SaveStatusIndicator fieldName="model" />
                </div>
                <systemForm.Field name="modelProvider">
                  {(providerField) => (
                    <systemForm.Field name="modelName">
                      {(modelField) => {
                        type ModelOption = {
                          provider: "openai" | "anthropic";
                          model: string;
                        };

                        const availableModels: ModelOption[] = [
                          ...AVAILABLE_MODELS,
                        ];

                        const currentSelection = availableModels.find(
                          (m) =>
                            m.provider === providerField.state.value &&
                            m.model === modelField.state.value,
                        );

                        const currentValue: ModelOption = currentSelection || {
                          provider: providerField.state.value as
                            | "openai"
                            | "anthropic",
                          model: modelField.state.value,
                        };

                        return (
                          <Combobox
                            items={availableModels}
                            value={currentValue}
                            onChange={(selectedModel) => {
                              providerField.handleChange(
                                selectedModel.provider,
                              );
                              modelField.handleChange(selectedModel.model);
                              setEditingField("model");
                              handleAutoSave({
                                modelProvider: selectedModel.provider,
                                modelName: selectedModel.model,
                              });
                            }}
                            getItemKey={(item) =>
                              `${item.provider}:${item.model}`
                            }
                            getSearchableText={(item) =>
                              `${item.provider} ${item.model}`
                            }
                            renderTrigger={(item) => (
                              <span className="font-mono">
                                {item
                                  ? `${item.provider}/${item.model}`
                                  : "Select model..."}
                              </span>
                            )}
                            renderItem={(item, isSelected) => (
                              <span
                                className={`font-mono ${isSelected ? "font-medium" : ""}`}
                              >
                                {item.provider}/{item.model}
                              </span>
                            )}
                            placeholder="Select model..."
                            emptyMessage="No models found."
                            className="font-mono"
                          />
                        );
                      }}
                    </systemForm.Field>
                  )}
                </systemForm.Field>
                <div className="text-xs text-muted-foreground">
                  Provider and model selection
                </div>
              </div>

              {/* System Prompt */}
              <systemForm.Field name="systemPrompt">
                {(field) => (
                  <div className="space-y-2 flex-1 flex flex-col">
                    <div className="flex items-center justify-between">
                      <label
                        className="text-sm font-medium"
                        htmlFor="systemPrompt"
                      >
                        System Prompt
                      </label>
                      <SaveStatusIndicator fieldName="systemPrompt" />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Instructions that guide the agent's behavior
                    </p>
                    <Textarea
                      id="systemPrompt"
                      value={field.state.value}
                      onChange={(e) => {
                        field.handleChange(e.target.value);
                        setEditingField("systemPrompt");
                        debouncedAutoSave({
                          systemPrompt: e.target.value,
                        });
                      }}
                      onBlur={field.handleBlur}
                      placeholder="Enter system prompt"
                      className="flex-1 font-mono text-sm resize-none"
                    />
                    {field.state.meta.errors && (
                      <div className="text-sm text-red-600">
                        {field.state.meta.errors.join(", ")}
                      </div>
                    )}
                  </div>
                )}
              </systemForm.Field>
            </div>
          );
        case "tools":
          return (
            <div className="space-y-6">
              <McpServersTable agentName={agentName} />
              <ToolsTable agentName={agentName} />
            </div>
          );
        case "portals":
          return (
            <div className="space-y-6">
              <div className="space-y-3">
                <div className="space-y-1">
                  <h3 className="text-sm font-medium">Portals</h3>
                  <p className="text-xs text-muted-foreground">
                    Configure how humans and other agents can connect to and
                    interact with this agent
                  </p>
                </div>
              </div>

              {/* Slack Integration */}
              <div className="space-y-2">
                <div className="text-sm font-medium">Slack Integration</div>
                <SlackIntegration agentName={agentName} />
              </div>
            </div>
          );
        case "console":
          return (
            <Suspense
              fallback={
                <div className="flex items-center justify-center h-full">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading chat...
                  </div>
                </div>
              }
            >
              {agent && (
                <MemoizedAgentChat
                  agentName={agentConfig?.name || agentName}
                  agent={agent}
                />
              )}
            </Suspense>
          );
        case "logs":
          return <AgentLogsTable agentName={agentName} />;

        case "api":
          return (
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">API Reference</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Programmatic access to this agent
                </p>
                <div className="space-y-3">
                  <div className="bg-muted/50 rounded-lg p-3 border">
                    <h4 className="font-medium mb-2">Endpoints</h4>
                    <ul className="space-y-1 text-sm font-mono">
                      <li className="flex items-start gap-2">
                        •
                        <span>
                          POST /api/agents/pavilion-agent/{agentName}/chat
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        •
                        <span>
                          GET /api/agents/pavilion-agent/{agentName}/config
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        •
                        <span>
                          WebSocket /api/agents/pavilion-agent/{agentName}/ws
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 border">
                    <h4 className="font-medium mb-2">Authentication</h4>
                    <ul className="space-y-1 text-sm">
                      <li className="flex items-start gap-2">
                        •<span>Bearer token authentication required</span>
                      </li>
                      <li className="flex items-start gap-2">
                        •<span>Include Authorization header in requests</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          );
        default:
          return null;
      }
    },
    [
      agentName,
      agent,
      agentConfig,
      systemForm,
      showDangerZone,
      handleAutoSave,
      debouncedAutoSave,
      SaveStatusIndicator,
    ],
  );

  return (
    <>
      <div className="flex flex-col h-full overflow-hidden bg-background max-h-screen">
        {/* Mobile header with sidebar trigger */}
        <div className="flex items-center gap-2 p-4 border-b md:hidden h-12">
          <FpSidebarTrigger />
          <h1 className="font-medium">{agentConfig?.name || agentName}</h1>
        </div>

        {/* Desktop: ResizablePanel layout like agents-ui */}
        <div className="hidden md:grid md:grid-cols-[1fr_1px_1fr] h-full overflow-hidden">
          {/* Left Panel - System/Tools */}
          <div className="flex flex-col h-full overflow-hidden">
            <FpTabs
              value={leftTab}
              onValueChange={(value) => updateTabInUrl(value as Tab)}
              className="grid grid-rows-[auto_1fr] h-full overflow-hidden"
            >
              <FpTabsList className="flex items-center px-5 text-muted-foreground w-full justify-start rounded-none border-b bg-transparent h-12">
                <FpTabsTrigger value="system" className="flex gap-2">
                  <Settings className="h-4 w-4" />
                  System
                </FpTabsTrigger>
                <FpTabsTrigger value="tools" className="flex gap-2">
                  <Wrench className="h-4 w-4" />
                  Tools
                </FpTabsTrigger>
                <FpTabsTrigger value="portals" className="flex gap-2">
                  <BrainCircuit className="h-4 w-4" />
                  Portals
                </FpTabsTrigger>
              </FpTabsList>

              <FpTabsContent
                value="system"
                className="min-h-0 overflow-y-auto px-0 py-0"
              >
                <div className="p-4 h-full flex flex-col">
                  <div className="flex-1 flex flex-col">
                    {renderTabContent("system")}
                  </div>
                </div>
              </FpTabsContent>

              <FpTabsContent
                value="tools"
                className="min-h-0 overflow-y-auto px-0 py-0"
              >
                <div className="p-4">{renderTabContent("tools")}</div>
              </FpTabsContent>

              <FpTabsContent
                value="portals"
                className="min-h-0 overflow-y-auto px-0 py-0"
              >
                <div className="p-4">{renderTabContent("portals")}</div>
              </FpTabsContent>
            </FpTabs>
          </div>

          {/* Separator */}
          <div className="w-[1px] bg-border" />

          {/* Right Panel - Console/How */}
          <div className="flex flex-col h-full overflow-hidden">
            <FpTabs
              value={rightTab}
              onValueChange={(value) => updateTabInUrl(value as Tab)}
              className="grid grid-rows-[auto_1fr] h-full overflow-hidden"
            >
              <FpTabsList className="inline-flex items-center px-5 text-muted-foreground w-full justify-start rounded-none border-b gap-1 bg-transparent h-12">
                <FpTabsTrigger value="console" className="flex gap-2">
                  <Terminal className="h-4 w-4" />
                  Console
                </FpTabsTrigger>
                <FpTabsTrigger value="logs" className="flex gap-2">
                  <Logs className="h-4 w-4" />
                  Logs
                </FpTabsTrigger>
                <FpTabsTrigger value="api" className="flex gap-2">
                  <CodeXml className="h-4 w-4" />
                  API
                </FpTabsTrigger>
              </FpTabsList>

              <FpTabsContent
                value="console"
                className="min-h-0 overflow-hidden px-0 py-0 flex flex-col"
              >
                <div className="flex-1 flex flex-col">
                  {renderTabContent("console")}
                </div>
              </FpTabsContent>

              <FpTabsContent
                value="logs"
                className="min-h-0 overflow-hidden px-0 py-0 flex flex-col"
              >
                <div className="p-4 flex-1 flex flex-col min-h-0">
                  {renderTabContent("logs")}
                </div>
              </FpTabsContent>

              <FpTabsContent
                value="api"
                className="min-h-0 overflow-y-auto px-0 py-0"
              >
                <div className="p-4">{renderTabContent("api")}</div>
              </FpTabsContent>
            </FpTabs>
          </div>
        </div>

        {/* Mobile: Single column with all tabs */}
        <div className="md:hidden flex flex-col flex-1 overflow-hidden">
          <FpTabs
            value={activeTab}
            onValueChange={(value) => updateTabInUrl(value as Tab)}
            className="grid grid-rows-[auto_1fr] h-full overflow-hidden"
          >
            <FpTabsList className="items-center px-5 text-muted-foreground w-full justify-start rounded-none border-b gap-1 bg-transparent h-12 grid grid-cols-5">
              <FpTabsTrigger value="system" className="flex gap-1">
                <Settings className="h-4 w-4" />
                System
              </FpTabsTrigger>
              <FpTabsTrigger value="tools" className="flex gap-1">
                <Wrench className="h-4 w-4" />
                Tools
              </FpTabsTrigger>
              <FpTabsTrigger value="portals" className="flex gap-1">
                <BrainCircuit className="h-4 w-4" />
                Portals
              </FpTabsTrigger>
              <FpTabsTrigger value="console" className="flex gap-1">
                <Terminal className="h-4 w-4" />
                Console
              </FpTabsTrigger>
              <FpTabsContent
                value="logs"
                className="min-h-0 overflow-y-auto px-0 py-0"
              >
                <div className="p-4">{renderTabContent("logs")}</div>
              </FpTabsContent>
              <FpTabsTrigger value="api" className="flex gap-1">
                <CodeXml className="h-4 w-4" />
                API
              </FpTabsTrigger>
            </FpTabsList>

            <FpTabsContent
              value="system"
              className="min-h-0 overflow-y-auto px-0 py-0"
            >
              <div className="p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div>
                    <h1 className="font-medium">
                      {agentConfig?.name || agentName}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      {agentConfig?.description || "AI Assistant"}
                    </p>
                  </div>
                </div>
                {renderTabContent("system")}
              </div>
            </FpTabsContent>

            <FpTabsContent
              value="tools"
              className="min-h-0 overflow-y-auto px-0 py-0"
            >
              <div className="p-4">{renderTabContent("tools")}</div>
            </FpTabsContent>

            <FpTabsContent
              value="portals"
              className="min-h-0 overflow-y-auto px-0 py-0"
            >
              <div className="p-4">{renderTabContent("portals")}</div>
            </FpTabsContent>

            <FpTabsContent
              value="console"
              className="min-h-0 overflow-hidden px-0 py-0 flex flex-col"
            >
              <div className="flex-1 flex flex-col">
                {renderTabContent("console")}
              </div>
            </FpTabsContent>

            <FpTabsContent
              value="logs"
              className="min-h-0 overflow-hidden px-0 py-0 flex flex-col"
            >
              <div className="p-4 flex-1 flex flex-col min-h-0">
                {renderTabContent("logs")}
              </div>
            </FpTabsContent>

            <FpTabsContent
              value="api"
              className="min-h-0 overflow-y-auto px-0 py-0"
            >
              <div className="p-4">{renderTabContent("api")}</div>
            </FpTabsContent>
          </FpTabs>
        </div>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the agent "
              {agentConfig?.name || agentName}" and all its conversations. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteAgentMutation.mutate(agentConfig.name);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
